import {
  getAndroidPlayStoreUrl,
  getIosAppStoreUrl,
  type MobilePlatform,
} from "@/lib/mobile-app-banner";
import type { OrigemAquisicao } from "@/lib/origem-aquisicao";

export const SYSIGREJA_PLANOS_LOGIN_ID = 12;
export const SITE_ORIGIN = "https://sysigreja.com";

/** Página pública do plano (sem login). */
export function buildPlanoPublicoPath(grupoId: number): string {
  return `/devocional/${grupoId}`;
}

/** Rota do plano no sistema (web logada). O login/cadastro já devolve a pessoa para cá. */
export function buildPlanoSistemaPath(grupoId: number): string {
  return `/sistema/devocionais/grupos/${grupoId}?loginId=${SYSIGREJA_PLANOS_LOGIN_ID}&mode=devocionais`;
}

export const ORIGEM_VIDEO_COMPARTILHADO = "share";

/** Tela cheia do vídeo do Devocional na web logada. */
export function buildVideoSistemaPath(videoId: number, opcoes?: { compartilhado?: boolean }): string {
  const params = new URLSearchParams({ mode: "devocionais" });
  if (opcoes?.compartilhado) params.set("origem", ORIGEM_VIDEO_COMPARTILHADO);
  return `/sistema/devocionais/videos/${videoId}?${params.toString()}`;
}

/** Login web com `redirect` até o plano escolhido. */
export function buildLoginPathForPlano(grupoId: number): string {
  return `/login?redirect=${encodeURIComponent(buildPlanoSistemaPath(grupoId))}`;
}

/** Scheme nativo — o app (OTA) mapeia `/devocional/:id` para o grupo. */
export function buildPlanoAppScheme(grupoId: number): string {
  return `sysigreja://devocional/${grupoId}`;
}

/** Extrai o id do plano de `/devocional/:id`, `/d/:id` ou da rota logada do grupo. */
export function parseGrupoIdDoRedirect(path: string | null | undefined): number | null {
  if (!path) return null;
  const pathname = path.trim().split("?")[0] ?? "";
  const publico = /^\/(?:devocional|d)\/(\d{1,9})\/?$/.exec(pathname);
  if (publico) return Number(publico[1]);
  const sistema = /^\/sistema\/devocionais\/grupos\/(\d+)\/?$/.exec(pathname);
  if (sistema) return Number(sistema[1]);
  return null;
}

/** Link que vai para a área de transferência no iOS; o app lê na 1ª abertura. */
export function buildPlanoClipboardLink(
  grupoId: number,
  origem: Pick<OrigemAquisicao, "utm_source" | "utm_medium" | "utm_campaign"> | null,
): string {
  const params = new URLSearchParams();
  if (origem?.utm_source) params.set("utm_source", origem.utm_source);
  if (origem?.utm_medium) params.set("utm_medium", origem.utm_medium);
  if (origem?.utm_campaign) params.set("utm_campaign", origem.utm_campaign);
  const qs = params.toString();
  return `${SITE_ORIGIN}/devocional/${grupoId}${qs ? `?${qs}` : ""}`;
}

/** Play Store com install referrer: o app lê `grupo` + utm_* na primeira abertura. */
export function buildPlanoPlayStoreUrl(
  grupoId: number,
  origem: Pick<OrigemAquisicao, "utm_source" | "utm_medium" | "utm_campaign"> | null,
  storeUrl: string = getAndroidPlayStoreUrl(),
): string {
  try {
    const url = new URL(storeUrl);
    const referrer = new URLSearchParams({
      grupo: String(grupoId),
      utm_source: origem?.utm_source || "site",
      utm_medium: origem?.utm_medium || "devocional_publico",
      utm_campaign: origem?.utm_campaign || `dev-${grupoId}`,
    });
    url.searchParams.set("referrer", referrer.toString());
    return url.toString();
  } catch {
    return storeUrl;
  }
}

export function buildPlanoStoreUrl(
  platform: MobilePlatform,
  grupoId: number,
  origem: Pick<OrigemAquisicao, "utm_source" | "utm_medium" | "utm_campaign"> | null,
): string | null {
  if (platform === "android") return buildPlanoPlayStoreUrl(grupoId, origem);
  if (platform === "ios") return getIosAppStoreUrl();
  return null;
}

/** Texto da descrição sem HTML, em parágrafos. */
export function descricaoEmParagrafos(descricao: string | null | undefined): string[] {
  if (!descricao) return [];
  const texto = descricao
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");
  return texto
    .split(/\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
}

const APP_OPEN_FALLBACK_MS = 1600;

/**
 * Tenta abrir o app pelo scheme; se a página continuar visível, chama `onFallback`
 * (loja). Mesmo padrão do `openNativeAppOrStore` da sala da igreja.
 */
export function tentarAbrirApp(scheme: string, onFallback: () => void) {
  let saiu = false;
  const marcar = () => {
    saiu = true;
  };
  document.addEventListener("visibilitychange", marcar);
  window.addEventListener("pagehide", marcar);
  window.addEventListener("blur", marcar);

  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.src = scheme;
  document.body.appendChild(iframe);

  window.setTimeout(() => {
    document.removeEventListener("visibilitychange", marcar);
    window.removeEventListener("pagehide", marcar);
    window.removeEventListener("blur", marcar);
    try {
      document.body.removeChild(iframe);
    } catch {
      /* ignore */
    }
    if (!saiu && !document.hidden) onFallback();
  }, APP_OPEN_FALLBACK_MS);
}
